import React from 'react';
import {defineMessages} from 'react-intl';
import {SortOrder} from '@igp/shared/api';
import {useAutoMapper} from '@igp/shared/auto-mapper';
import {ErrorModal} from '@igp/shared/components/error';
import {LayoutQueue, PageHeader, Toolbar, ToolbarItems, ToolbarItemsContainer} from '@igp/shared/components/layout';
import {withTraceErrorBoundary} from '@igp/shared/otel';
import type {ViewType} from '@igp/shared/redux/view';
import {getNonEmptyValueValidator} from '@igp/shared/redux/view';
import {getTodayDateRange, getUTCTimestamp, momentToTimestampSeconds} from '@igp/shared/utils';

import type {AgentLevelReportV2ViewModelsKeys, DataGridAgentLevelReportV2Keys} from '@features/agent-level-report-v2';
import {
    AgentLevelReportV2DownloadCsvButton,
    AgentLevelReportV2DownloadViewModelsKeysTypeName,
    agentLevelReportV2MappingKeys,
    AgentLevelReportV2QueryFilters,
    DataGridAgentLevelReportV2,
    firstAvailableDate,
    useAgentLevelReportV2,
    useAgentLevelReportV2Settings,
} from '@features/agent-level-report-v2';

const localized = defineMessages({
    title: {
        id: 'AgentLevelReportDaily_title',
        defaultMessage: 'Agent Level Report Daily',
    },
});

function AgentLevelReportDailyInner() {
    const mapper = useAutoMapper();
    const viewType: ViewType = 'AgentLevelReportDaily';
    const columns: DataGridAgentLevelReportV2Keys[] = [
        'agentUid',
        'agentUsername',
        'pokerNlheSdNetShare',
        'pokerMttNetShare',
        'pokerCashPloNetShare',
        'casinoNetShare',
        'globalSpinsNetShare',
        'sportsNetShare',
        'totalNetShare',
        'details',
    ];
    const fields: AgentLevelReportV2ViewModelsKeys[] = mapper.map<DataGridAgentLevelReportV2Keys[], AgentLevelReportV2ViewModelsKeys[]>(
        columns,
        agentLevelReportV2MappingKeys.ColumnKeys,
        agentLevelReportV2MappingKeys.ViewModelsKeys
    );
    const [defaultStartDate, defaultEndDate] = getTodayDateRange();
    const {
        items,
        rawItems,
        totalCount,
        searchFilter,
        filterString,
        handleFilterChange,
        handlePageChange,
        handlePageSizeChange,
        handleSortChange,
    } = useAgentLevelReportV2({
        viewType,
        fields,
        defaultPaging: {page: 1, pageSize: 25},
        defaultFilters: [
            {
                key: 'is_downstream',
                value: true,
            },
            {
                key: 'type',
                value: 'daily',
            },
            {
                key: 'daily_aggregation_range.from',
                value: defaultStartDate,
            },
            {
                key: 'daily_aggregation_range.to',
                value: defaultEndDate,
            },
        ],
        hiddenFilters: [{key: 'daily_aggregation_range.min', value: momentToTimestampSeconds(firstAvailableDate)}],
        defaultSorting: [{field: 'agentUid', sort: SortOrder.Desc}],
        validateFilter: filter =>
            getNonEmptyValueValidator('is_downstream')(filter) && filter?.find(f => f.key === 'type')?.value?.toString() === 'daily',
        syncWithUrl: true,
    });
    const pinnedColumns: DataGridAgentLevelReportV2Keys[] = ['agentUid', 'agentUsername'];
    const {visibleColumns, onVisibleColumnsChange} = useAgentLevelReportV2Settings({
        settingId: viewType,
        defaultColumns: columns,
        pinnedColumns,
    });
    const downloadableColumnFields: AgentLevelReportV2ViewModelsKeys[] = mapper.map<
        DataGridAgentLevelReportV2Keys[],
        AgentLevelReportV2ViewModelsKeys[]
    >(visibleColumns, agentLevelReportV2MappingKeys.ColumnKeys, AgentLevelReportV2DownloadViewModelsKeysTypeName);
    const startDate = Number.parseInt(searchFilter?.filter?.find(f => f.key === 'daily_aggregation_range.from')?.value);
    const endDateString = searchFilter?.filter?.find(f => f.key === 'daily_aggregation_range.to')?.value?.toString();
    const endDate = endDateString === 'now' ? getUTCTimestamp().seconds : Number.parseInt(endDateString);
    return (
        <LayoutQueue
            header={
                <PageHeader
                    header={localized.title}
                    actions={
                        <AgentLevelReportV2DownloadCsvButton
                            keys={downloadableColumnFields}
                            filter={filterString}
                            filename={localized.title}
                            total={totalCount}
                            disabled={!totalCount}
                            mode="server"
                        />
                    }
                />
            }
            toolbar={
                <Toolbar>
                    <ToolbarItemsContainer primary>
                        <ToolbarItems streched>
                            <AgentLevelReportV2QueryFilters
                                model={filterString}
                                onChange={handleFilterChange}
                                availableFilters={['agentUidUsername', 'dailyAggregationRange']}
                            />
                        </ToolbarItems>
                    </ToolbarItemsContainer>
                </Toolbar>
            }
            body={
                <DataGridAgentLevelReportV2
                    configurableColumns={{
                        pinnedColumns,
                        visibleColumns,
                        onVisibleColumnsChange,
                    }}
                    columns={columns}
                    rows={items}
                    rawModels={rawItems}
                    paging={{
                        ...searchFilter?.paging,
                        rowCount: totalCount,
                    }}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    sortModel={{
                        sortModel: searchFilter?.sorting,
                    }}
                    onSortChange={handleSortChange}
                    type={'daily'}
                    startDate={startDate}
                    endDate={endDate}
                />
            }
        />
    );
}

export const AgentLevelReportDaily = withTraceErrorBoundary(AgentLevelReportDailyInner, ErrorModal);
